import React from "react";
import { Accordion } from "react-bootstrap";
import { imgUrl } from "../config/api";
import { CapitalizeAllWords } from "../Functions/TextTransform";
import { formatCombinedOutsideMountLabel, normalizeOutsideMountSubType } from "./draperyUi";
import { DkCheckoutFabricDetails } from "./DkSideCartFabricDisplay";

const HARDWARE_FA = {
  "I Have 28mm Rod": "من میله پردهِ ۲۸ میل دارم",
  "I Have 25mm Rod": "من میله پردهِ ۲۵ میل دارم",
  "Standard Lining": "آستردوزی استاندارد",
  "I Have Others": "من چیز دیگری دارم",
  "I Have Motorized Track": "من تراک موتوری دارم",
  "I Have Track With Cord Draw": "من تراک اتومات دارم",
  "I Have Standard Track": "من تراک استاندارد دارم",
  Track: "تراک",
  Rod: "میله پردهِ",
  Ring: "رینگ",
};

const BATON_FA = {
  "Baton 45cm": "باتون ۴۵ س‌م",
  "Baton 30cm": "باتون ۳۰ س‌م",
  "Baton 50cm": "باتون ۵۰ س‌م",
};

const DRAPERY_HARDWARE_FIELDS = {
  typeKeys: ["DraperyHardware", "hardwareDrapery"],
  extraKeys: ["RailDesignFaA", "RailDesignEnA", "RailColorFaA", "RailColorEnA", "BatonOptionA"],
  designFa: "RailDesignFaA",
  designEn: "RailDesignEnA",
  colorFa: "RailColorFaA",
  colorEn: "RailColorEnA",
  batonKey: "BatonOptionA",
};

const SHEER_HARDWARE_FIELDS = {
  typeKeys: ["SheerHardware", "hardwareSheer"],
  extraKeys: ["RailDesignFaB", "RailDesignEnB", "RailColorFaB", "RailColorEnB", "BatonOptionB"],
  designFa: "RailDesignFaB",
  designEn: "RailDesignEnB",
  colorFa: "RailColorFaB",
  colorEn: "RailColorEnB",
  batonKey: "BatonOptionB",
  layerKeys: ["FabricDesignEn2"],
};

const PRIVACY_HARDWARE_FIELDS = {
  typeKeys: ["PrivacyLayerHardware", "hardwarePrivacyLayer"],
  extraKeys: ["RailDesignFaC", "RailDesignEnC", "RailColorFaC", "RailColorEnC", "BatonOptionC"],
  designFa: "RailDesignFaC",
  designEn: "RailDesignEnC",
  colorFa: "RailColorFaC",
  colorEn: "RailColorEnC",
  batonKey: "BatonOptionC",
  controlKey: "ControlPositionC",
  layerKeys: ["FabricDesignEn3", "SheersDesignEn"],
};

export default function ProductTowComponent({
  product2,
  pageLanguage,
  NumberToPersianWord,
  convertToPersian,
  t,
  GetPrice,
  isTabletOrMobile,
  ContextAwareToggleViewDetails,
  capitalizeFirstLetter,
}) {
  const shouldShow0325DisplayField = (preorderText, fieldKey) => {
    if (!preorderText || preorderText.SewingModelId !== "0325") {
      return true;
    }
    const fieldConditions = {
      RollType: { Headrail: "Exposed", HemStyle: "Straight", RollType: "Standard" },
      BracketType: { Headrail: "Exposed", HemStyle: "Straight", BracketType: "Round Edge" },
      BracketColor: { Headrail: "Exposed", HemStyle: "Straight", BracketColor: "Satin Brass" },
      BottomBarStyle: { Headrail: "Exposed", HemStyle: "Straight", BottomBarStyle: "Sewn-In" },
    };
    const conditions = fieldConditions[fieldKey];
    if (!conditions) {
      return true;
    }
    return Object.keys(conditions).every(
      (conditionKey) => preorderText[conditionKey] === conditions[conditionKey]
    );
  };

  const isHardwarePartVisible = (value) => Boolean(value) && String(value).trim() !== "None";

  const getHardwareType = (preorderText, typeKeys) => {
    if (!preorderText) return null;
    return typeKeys.map((key) => preorderText[key]).find(isHardwarePartVisible) || null;
  };

  const isValidLayerValue = (value) =>
    Boolean(value) && value !== "undefined" && value !== "null" && String(value).trim() !== "";

  const hasHardwareLayer = (preorderText, fields) => {
    if (!fields.layerKeys?.length) return true;
    return fields.layerKeys.some((key) => isValidLayerValue(preorderText[key]));
  };

  const shouldShowLayerHardware = (preorderText, fields) => {
    if (!preorderText || !hasHardwareLayer(preorderText, fields)) return false;
    const [primaryTypeKey, fallbackTypeKey] = fields.typeKeys;
    if (isHardwarePartVisible(preorderText[fallbackTypeKey])) return true;
    if (preorderText[primaryTypeKey] === "None") return false;
    return [primaryTypeKey, ...fields.extraKeys].some((key) =>
      isHardwarePartVisible(preorderText[key])
    );
  };

  const getLayerHardwareLabel = (preorderText, language, fields) => {
    if (!preorderText) return "";
    const isFa = language === "fa";
    const hardwareType = getHardwareType(preorderText, fields.typeKeys);
    const batonValue = preorderText[fields.batonKey];
    const parts = [
      hardwareType ? (isFa ? HARDWARE_FA[hardwareType] || hardwareType : hardwareType) : null,
      isFa ? preorderText[fields.designFa] : preorderText[fields.designEn],
      isFa ? preorderText[fields.colorFa] : preorderText[fields.colorEn],
      isHardwarePartVisible(batonValue)
        ? isFa
          ? BATON_FA[batonValue] || batonValue
          : batonValue
        : null,
      fields.controlKey && isHardwarePartVisible(batonValue)
        ? preorderText[fields.controlKey]
        : null,
    ];
    return parts.filter(isHardwarePartVisible).join(" / ");
  };

  return (
    <div>
      {product2
        .slice()
        ?.filter((item) => item.TypeId !== 6402)
        .sort(
          (a, b) =>
            (b.CartDetailId ?? 0) - (a.CartDetailId ?? 0) ||
            (b.SewingOrderId ?? 0) - (a.SewingOrderId ?? 0)
        )
        .map((product, i) => (
          <li className="checkout_item_container" key={i}>
            <div className="checkout_item_image_container">
              {product.TypeId === 6401 ? (
                <img
                  src={imgUrl(product.PhotoUrl)}
                  alt={product.ProductName}
                  className="checkout_item_img"
                />
              ) : (
                <img
                  src={imgUrl(product.SewingOrder.PreorderText.PhotoUrl)}
                  alt=""
                  className="checkout_item_img"
                />
              )}

              <p className="checkout_item_image_qty">
                {pageLanguage === "fa"
                  ? NumberToPersianWord.convertEnToPe(`${product.Count}`)
                  : product.Count}
              </p>
            </div>
            <div
              className={`checkout_item_desc_container checkout_item_container_shades 1 ${
                product.TypeId === 6401 ? "checkout_item_container_shades_products" : ""
              }`}
            >
              <div className="checkout_item 3">
                <h1 className="checkout_item_name">
                  {product.TypeId === 6401 ? (
                    <p style={{ display: "flex" }}>
                      <p>{pageLanguage === "fa" ? product.DesignName : product.DesignEnName}</p>
                      {(product.ProductGroupId === "100203" ||
                        product.ProductGroupId === "100201" ||
                        product.ProductGroupId === "100204" ||
                        product.ProductGroupId === "100202" ||
                        product.ProductGroupId === "100205") && (
                        <p style={{ padding: "0 0.2rem" }}>
                          {pageLanguage === "fa"
                            ? product.SewingModelName
                            : CapitalizeAllWords(product.SewingModelEnName)}
                        </p>
                      )}
                    </p>
                  ) : (
                    <>
                      {pageLanguage === "fa"
                        ? convertToPersian(product.SewingOrder.PreorderText.ModelNameFa) +
                          " سفارشی "
                        : "Custom " + product.SewingOrder.PreorderText.ModelNameEn}
                    </>
                  )}
                </h1>
                {product.TypeId === 6401 ? (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      className="checkout_item_price"
                      style={{
                        textDecoration:
                          product.Amount !== product.PayableAmount ? "line-through" : "none",
                      }}
                    >
                      {product.Amount === 0
                        ? t("Free")
                        : GetPrice(
                            product.Amount * product.Count,
                            pageLanguage,
                            t("TOMANS"),
                            isTabletOrMobile
                          )}
                    </span>
                    {product.Amount !== product.PayableAmount && (
                      <span className="checkout_item_price" style={{ color: "#ca6667" }}>
                        {product.PayableAmount === 0
                          ? t("Free")
                          : GetPrice(
                              product.PayableAmount * product.Count,
                              pageLanguage,
                              t("TOMANS"),
                              isTabletOrMobile
                            )}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="checkout_item_price_section 3">
                    {product.Amount !== product.PayableAmount ? (
                      <>
                        <span
                          className="checkout_item_price checkout_item_price_with_discount 1"
                          style={{ textDecoration: "line-through" }}
                        >
                          {GetPrice(
                            product.Amount * product.Count,
                            pageLanguage,
                            t("TOMANS"),
                            isTabletOrMobile
                          )}
                        </span>
                        <span className="checkout_item_price 2" style={{ color: "#ca6667" }}>
                          {GetPrice(
                            product.PayableAmount * product.Count,
                            pageLanguage,
                            t("TOMANS"),
                            isTabletOrMobile
                          )}
                        </span>
                      </>
                    ) : (
                      <span className="checkout_item_price 3">
                        {GetPrice(
                          product.DiscountedPrice,
                          pageLanguage,
                          t("TOMANS"),
                          isTabletOrMobile
                        )}
                      </span>
                    )}

                    {product?.SewingOrder?.PreorderText?.DiscountedPrice != null &&
                      product?.SewingOrder?.PreorderText?.DiscountedPrice !== 0 &&
                      product?.SewingOrder?.PreorderText?.DiscountedPrice !==
                        NaN(
                          <span className="checkout_item_price 4">
                            {GetPrice(
                              product?.SewingOrder?.PreorderText?.DiscountedPrice * product.Count,
                              pageLanguage,
                              t("TOMANS"),
                              isTabletOrMobile
                            )}
                          </span>
                        )}
                  </div>
                )}
              </div>
              {(() => {
                console.log(`product`, product);
              })()}
              <>
                {product.TypeId === 6403 ? (
                  <>
                    <span className="checkout_item_room_name">
                      {pageLanguage === "fa"
                        ? product.SewingOrder.PreorderText.RoomNameFa +
                          " / " +
                          product.SewingOrder.PreorderText.WindowDescription
                        : product.SewingOrder.PreorderText.RoomNameEn +
                          " / " +
                          product.SewingOrder.PreorderText.WindowDescription}
                    </span>
                    <Accordion>
                      <Accordion.Item eventKey="0">
                        <ContextAwareToggleViewDetails
                          eventKey="0"
                          textOnHide={t("View Details")}
                          textOnShow={t("Hide Details")}
                        />
                        <Accordion.Body className="basket_item_title_dropdown dk_curtain_preview_dropdown">
                          <div className="basket_item_title_container 141">
                            {product.SewingOrder.PreorderText.SewingModelId === "0099" && (
                              <h1 class="cart_agree_item_fabrics_sorted_title">
                                {t("General Details")}
                              </h1>
                            )}
                            {product.SewingOrder.PreorderText.MountPositionId &&
                              product.SewingOrder.PreorderText.SewingModelId !== "0099" && (
                                <>
                                  {product.SewingOrder.PreorderText.SewingModelId === "0326" ? (
                                    <DkCheckoutFabricDetails
                                      product={{
                                        PreorderText: product.SewingOrder.PreorderText,
                                        ...product.SewingOrder,
                                        SewingOrderDetails:
                                          product.SewingOrder.SewingOrderDetails ||
                                          product.SewingOrderDetails,
                                      }}
                                      pageLanguage={pageLanguage}
                                      t={t}
                                    />
                                  ) : (
                                    <div
                                      className={`lable_Details 1 ${
                                        pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                      }`}
                                    >
                                      <p>{t("Fabric & Color")}</p>
                                      <span>
                                        {pageLanguage === "fa"
                                          ? (product.SewingOrder.PreorderText["FabricDesignFa"]
                                              ? product.SewingOrder.PreorderText["FabricDesignFa"]
                                              : product.SewingOrder.PreorderText[
                                                  "FabricDesignFa2"
                                                ]) +
                                            " / " +
                                            (product.SewingOrder.PreorderText["FabricColorFa"]
                                              ? product.SewingOrder.PreorderText["FabricColorFa"]
                                              : product.SewingOrder.PreorderText[
                                                  "FabricColorFa2"
                                                ])
                                          : CapitalizeAllWords(
                                              (product.SewingOrder.PreorderText["FabricDesignEn"]
                                                ? product.SewingOrder.PreorderText["FabricDesignEn"]
                                                : product.SewingOrder.PreorderText[
                                                    "FabricDesignEn2"
                                                  ]) +
                                                " / " +
                                                (product.SewingOrder.PreorderText["FabricColorEn"]
                                                  ? product.SewingOrder.PreorderText["FabricColorEn"]
                                                  : product.SewingOrder.PreorderText[
                                                      "FabricColorEn2"
                                                    ])
                                            )}
                                      </span>
                                    </div>
                                  )}
                                  <div
                                    className={`lable_Details ${
                                      pageLanguage === "fa" ? "lable_Details_rtl 1" : ""
                                    }`}
                                  >
                                    <p>{t("Mount Position")}</p>
                                    <span>
                                      {product.SewingOrder.PreorderText.MountPositionId ===
                                        4801 && <span>{t("Inside Mount")}</span>}
                                      {product.SewingOrder.PreorderText.MountPositionId ===
                                        4802 && (
                                        <span>
                                          {formatCombinedOutsideMountLabel(
                                            normalizeOutsideMountSubType(
                                              product.SewingOrder.PreorderText?.IsWalled ??
                                                product.IsWalled,
                                              product.installationLocation
                                            ),
                                            pageLanguage,
                                            t
                                          ) || t("Outside Mount")}
                                        </span>
                                      )}
                                      {product.SewingOrder.PreorderText.MountPositionId ===
                                        4803 && <span>{t("Moulding Mount")}</span>}
                                    </span>
                                  </div>
                                </>
                              )}

                            {product.SewingOrder.PreorderText.MountPositionId &&
                              product.SewingOrder.PreorderText.SewingModelId === "0099" && (
                                <div
                                  className={`lable_Details 2 ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Mount Position")}</p>
                                  <span>
                                    {product.SewingOrder.PreorderText.MountPositionId === 4801 && (
                                      <span>{t("Ceiling")}</span>
                                    )}
                                    {product.SewingOrder.PreorderText.MountPositionId === 4802 && (
                                      <span>{t("Wall")}</span>
                                    )}
                                    {product.SewingOrder.PreorderText.MountPositionId === 4803 && (
                                      <span>{t("Moulding")}</span>
                                    )}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.CurtainPositionId &&
                              product.SewingOrder.PreorderText.SewingModelId === "0099" && (
                                <div
                                  className={`lable_Details 3 ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Curtain Position")}</p>
                                  <span>
                                    {product.SewingOrder.PreorderText.CurtainPositionId ===
                                      4901 && <span>{t("Standard")}</span>}
                                    {product.SewingOrder.PreorderText.CurtainPositionId ===
                                      4902 && <span>{t("Wall To Wall")}</span>}
                                    {product.SewingOrder.PreorderText.CurtainPositionId ===
                                      4903 && <span>{t("Right Corner Window")}</span>}
                                    {product.SewingOrder.PreorderText.CurtainPositionId ===
                                      4904 && <span>{t("Left Corner Window")}</span>}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.WindowWidth && (
                              <div
                                className={`lable_Details 4 ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("WIDTH")}</p>
                                <span>
                                  {pageLanguage === "fa" ? (
                                    <>
                                      {new Intl.NumberFormat("fa-IR").format(
                                        product.SewingOrder.PreorderText.WindowWidth
                                      )}
                                    </>
                                  ) : (
                                    <>{product.SewingOrder.PreorderText.WindowWidth}</>
                                  )}
                                  <>{t("cm")}</>
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.WindowHeight && (
                              <div
                                className={`lable_Details 5 ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Height")}</p>
                                <span>
                                  {pageLanguage === "fa" ? (
                                    <>
                                      {new Intl.NumberFormat("fa-IR").format(
                                        product.SewingOrder.PreorderText.WindowHeight
                                      )}
                                    </>
                                  ) : (
                                    <>{product.SewingOrder.PreorderText.WindowHeight}</>
                                  )}
                                  <>{t("cm")}</>
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.FinishedLengthTypeId &&
                              product.SewingOrder.PreorderText.SewingModelId === "0099" && (
                                <div
                                  className={`lable_Details 6 ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Curtain Position")}</p>
                                  <span>
                                    {product.SewingOrder.PreorderText.FinishedLengthTypeId ===
                                      5401 && <span>{t("Sill")}</span>}
                                    {product.SewingOrder.PreorderText.FinishedLengthTypeId ===
                                      5402 && <span>{t("Apron")}</span>}
                                    {product.SewingOrder.PreorderText.FinishedLengthTypeId ===
                                      5403 && <span>{t("Floor")}</span>}
                                    {product.SewingOrder.PreorderText.FinishedLengthTypeId ===
                                      5404 && <span>{t("Slight Puddle")}</span>}
                                  </span>
                                </div>
                              )}
                            {/* Drapery */}
                            {(product.SewingOrder.PreorderText.SewingModelId === "0099" ||
                              product.SewingOrder.PreorderText.SewingModelId === "0321") &&
                              (product.SewingOrder.PreorderText.FabricDesignEn ||
                                product.FabricDesignEn) && (
                                <div style={{ marginTop: "0.5rem" }}>
                                  <hr />
                                  <h1
                                    class="cart_agree_item_fabrics_sorted_title"
                                    style={{ marginTop: "0.2rem" }}
                                  >
                                    {t("Drapery")}
                                  </h1>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.FabricDesignEn &&
                              product.SewingOrder.PreorderText.FabricDesignEn !== "undefined" &&
                              product.SewingOrder.PreorderText.FabricDesignEn !== "null" &&
                              product.SewingOrder.PreorderText.FabricDesignEn !== "" &&
                              product.SewingOrder.PreorderText.SewingModelId === "0099" && (
                                <div
                                  className={`lable_Details 7 ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Material & Color")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? product.SewingOrder.PreorderText.FabricDesignFa +
                                        "/" +
                                        product.SewingOrder.PreorderText.FabricColorFa
                                      : capitalizeFirstLetter(
                                          product.SewingOrder.PreorderText.FabricDesignEn
                                        ) +
                                        "/" +
                                        capitalizeFirstLetter(
                                          product.SewingOrder.PreorderText.FabricColorEn
                                        )}
                                  </span>
                                </div>
                              )}

                            {product.SewingOrder.PreorderText.BorderOrTrimDesignEn && (
                              <div
                                className={`lable_Details 8${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Decorative Border")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? [
                                        product.SewingOrder.PreorderText.BorderOrTrimDesignFa,
                                        product.SewingOrder.PreorderText.BorderOrTrimColorFa,
                                        product.SewingOrder.PreorderText.borderPosition ===
                                        "60cm bottom border"
                                          ? "حاشیه۶۰ سانتی پایین"
                                          : t(product.SewingOrder.PreorderText.borderPosition),
                                      ]
                                        .filter(
                                          (item) => item && item !== "undefined" && item !== "null"
                                        )
                                        .join("/")
                                    : [
                                        product.SewingOrder.PreorderText.BorderOrTrimDesignEn,
                                        product.SewingOrder.PreorderText.BorderOrTrimColorEn,
                                        t(product.SewingOrder.PreorderText.borderPosition),
                                      ]
                                        .filter(
                                          (item) => item && item !== "undefined" && item !== "null"
                                        )
                                        .join("/")}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.Lining && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Lining")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        Unlined: "بدون خط",
                                        "Standard Lining": "آستردوزی استاندارد",
                                        Right: "راست",
                                        "Privacy Lining": "آستردوزی ضخیم",
                                      }[product.SewingOrder.PreorderText.Lining] ||
                                      product.SewingOrder.PreorderText.Lining
                                    : product.SewingOrder.PreorderText.Lining}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.PanelCoverage &&
                              (product.SewingOrder.PreorderText.FabricDesignEn ||
                                product.FabricDesignEn) && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Panel Coverage")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? {
                                          Full: "پوششی",
                                          "Standard Lining": "آستردوزی استاندارد",
                                          Decorative: "دکوری",
                                        }[product.SewingOrder.PreorderText.PanelCoverage] ||
                                        product.SewingOrder.PreorderText.PanelCoverage
                                      : product.SewingOrder.PreorderText.PanelCoverage}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.PanelTypeA && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Panel Type")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        "Single Panel": "تک پنل، سمت چپ",
                                        "Standard Lining": "آستردوزی استاندارد",
                                        "Pair, Split Draw": "جفت پنل،دو طرف",
                                        "Single Panel, Right": "تک پنل، سمت راست",
                                      }[product.SewingOrder.PreorderText.PanelTypeA] ||
                                      product.SewingOrder.PreorderText.PanelTypeA
                                    : product.SewingOrder.PreorderText.PanelTypeA}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.GrommetFinish &&
                              (product.SewingOrder.PreorderText.FabricDesignEn ||
                                product.FabricDesignEn) && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Grommet Finish")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? {
                                          "Satin Brass": "طلایی خش‌دار",
                                          "Standard Lining": "آستردوزی استاندارد",
                                          "Satin Nickel": "نقره‌ای خش‌دار",
                                        }[product.SewingOrder.PreorderText.GrommetFinish] ||
                                        product.SewingOrder.PreorderText.GrommetFinish
                                      : product.SewingOrder.PreorderText.GrommetFinish}
                                  </span>
                                </div>
                              )}
                            {shouldShowLayerHardware(
                              product.SewingOrder?.PreorderText,
                              DRAPERY_HARDWARE_FIELDS
                            ) && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Hardware")}</p>
                                <span>
                                  {getLayerHardwareLabel(
                                    product.SewingOrder.PreorderText,
                                    pageLanguage,
                                    DRAPERY_HARDWARE_FIELDS
                                  )}
                                </span>
                              </div>
                            )}

                            {product.SewingOrder.PreorderText.HandCurtainNum && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Matching Tieback")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? `تعداد ${new Intl.NumberFormat("fa-IR").format(
                                        product.SewingOrder.PreorderText.HandCurtainNum
                                      )}`
                                    : `Qty ${product.SewingOrder.PreorderText.HandCurtainNum}`}
                                </span>
                              </div>
                            )}
                            {/* Sheer */}
                            {(product.SewingOrder.PreorderText.SewingModelId === "0099" ||
                              product.SewingOrder.PreorderText.SewingModelId === "0321") &&
                              (product.SewingOrder.PreorderText.MountPositionId !== 4801 ||
                                product.SewingOrder.PreorderText.MountPositionId === 4801) &&
                              product.SewingOrder.PreorderText.FabricDesignEn2 && (
                                <div style={{ marginTop: "0.5rem" }}>
                                  <hr />
                                  <h1
                                    class="cart_agree_item_fabrics_sorted_title"
                                    style={{ marginTop: "0.2rem" }}
                                  >
                                    {t("Sheer")}
                                  </h1>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.FabricDesignEn2 &&
                              product.SewingOrder.PreorderText.FabricDesignEn2 !== "undefined" &&
                              product.SewingOrder.PreorderText.FabricDesignEn2 !== "null" &&
                              product.SewingOrder.PreorderText.FabricDesignEn2 !== "" &&
                              product.SewingOrder.PreorderText.SewingModelId === "0099" &&
                              product.SewingOrder.PreorderText.MountPositionId !== 4801 && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Material & Color")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? product.SewingOrder.PreorderText.FabricDesignFa2 +
                                        "/" +
                                        product.SewingOrder.PreorderText.FabricColorFa2
                                      : product.SewingOrder.PreorderText.FabricDesignEn2 +
                                        "/" +
                                        product.SewingOrder.PreorderText.FabricColorEn2}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.FabricDesignEn2 &&
                              product.SewingOrder.PreorderText.FabricDesignEn2 !== "undefined" &&
                              product.SewingOrder.PreorderText.FabricDesignEn2 !== "null" &&
                              product.SewingOrder.PreorderText.FabricDesignEn2 !== "" &&
                              product.SewingOrder.PreorderText.SewingModelId === "0099" &&
                              product.SewingOrder.PreorderText.MountPositionId === 4801 && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Material & Color")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? product.SewingOrder.PreorderText.FabricDesignFa2 +
                                        "/" +
                                        product.SewingOrder.PreorderText.FabricColorFa2
                                      : capitalizeFirstLetter(
                                          product.SewingOrder.PreorderText.FabricDesignEn2
                                        ) +
                                        "/" +
                                        product.SewingOrder.PreorderText.FabricColorEn2}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.SheerHeaderStyle && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Header Style")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        Grommet: "پانجی",
                                        "Standard Lining": "آستردوزی استاندارد",
                                        Right: "راست",
                                        "Inverted Box Pleat": "پلیسه",
                                        "Pencil Pleat": "ساده",
                                        "Double European Pleat": "پلیسه دوچین اروپایی",
                                      }[product.SewingOrder.PreorderText.SheerHeaderStyle] ||
                                      product.SewingOrder.PreorderText.SheerHeaderStyle
                                    : product.SewingOrder.PreorderText.SheerHeaderStyle}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.PanelTypeB && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Panel Type")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        "Single Panel": "تک پنل، سمت چپ",
                                        "Standard Lining": "آستردوزی استاندارد",
                                        "Pair, Split Draw": "جفت پنل،دو طرف",
                                        "Single Panel, Right": "تک پنل، سمت راست",
                                      }[product.SewingOrder.PreorderText.PanelTypeB] ||
                                      product.SewingOrder.PreorderText.PanelTypeB
                                    : product.SewingOrder.PreorderText.PanelTypeB}
                                </span>
                              </div>
                            )}
                            {shouldShowLayerHardware(
                              product.SewingOrder?.PreorderText,
                              SHEER_HARDWARE_FIELDS
                            ) && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Hardware")}</p>
                                <span>
                                  {getLayerHardwareLabel(
                                    product.SewingOrder.PreorderText,
                                    pageLanguage,
                                    SHEER_HARDWARE_FIELDS
                                  )}
                                </span>
                              </div>
                            )}

                            {product.SewingOrder.PreorderText.HandCurtainNum2 && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Matching Tieback")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? `تعداد ${new Intl.NumberFormat("fa-IR").format(
                                        product.SewingOrder.PreorderText.HandCurtainNum2
                                      )}`
                                    : `Qty ${product.SewingOrder.PreorderText.HandCurtainNum2}`}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.MotorPositionB && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Motor Position")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? product.SewingOrder.PreorderText.MotorPositionB === "Right"
                                      ? "راست"
                                      : product.SewingOrder.PreorderText.MotorPositionB === "Left"
                                      ? "چپ"
                                      : product.SewingOrder.PreorderText.MotorPositionB
                                    : product.SewingOrder.PreorderText.MotorPositionB}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.RemoteNameB && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Remote Name")}</p>
                                <span>{product.SewingOrder.PreorderText.RemoteNameB}</span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.MotorChannelsB && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Motor Channels")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? product.SewingOrder.PreorderText.MotorChannelsB.join(
                                        ","
                                      ).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d])
                                    : product.SewingOrder.PreorderText.MotorChannelsB.join(",")}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.hasLead && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Lead Weight")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? product.SewingOrder.PreorderText.hasLead === "Included"
                                      ? "دارد"
                                      : product.SewingOrder.PreorderText.hasLead === "True"
                                      ? "دارد"
                                      : product.SewingOrder.PreorderText.hasLead === "Right"
                                      ? "راست"
                                      : product.SewingOrder.PreorderText.hasLead === "Left"
                                      ? "چپ"
                                      : product.SewingOrder.PreorderText.hasLead
                                    : product.SewingOrder.PreorderText.hasLead}
                                </span>
                              </div>
                            )}
                            {/* Privacy Layer */}
                            {(product.SewingOrder.PreorderText.SewingModelId === "0099" ||
                              product.SewingOrder.PreorderText.SewingModelId === "0321") &&
                              (product.SewingOrder.PreorderText.MountPositionId !== 4801 ||
                                product.SewingOrder.PreorderText.MountPositionId === 4801) &&
                              (product.SewingOrder.PreorderText.SheersDesignEn ||
                                product.SheersDesignEn) && (
                                <div style={{ marginTop: "0.5rem" }}>
                                  <hr />
                                  <h1
                                    class="cart_agree_item_fabrics_sorted_title"
                                    style={{ marginTop: "0.2rem" }}
                                  >
                                    {t("Privacy Layer")}
                                  </h1>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.FabricDesignEn3 &&
                              product.SewingOrder.PreorderText.FabricDesignEn3 !== "undefined" &&
                              product.SewingOrder.PreorderText.FabricDesignEn3 !== "null" &&
                              product.SewingOrder.PreorderText.FabricDesignEn3 !== "" &&
                              product.SewingOrder.PreorderText.SewingModelId === "0099" && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Material & Color")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? product.SewingOrder.PreorderText.FabricDesignFa3 +
                                        "/" +
                                        product.SewingOrder.PreorderText.FabricColorFa3
                                      : product.SewingOrder.PreorderText.FabricDesignEn3 +
                                        "/" +
                                        product.SewingOrder.PreorderText.FabricColorEn3}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.SheersDesignEn &&
                              product.SewingOrder.PreorderText.SheersDesignEn !== "undefined" &&
                              product.SewingOrder.PreorderText.SheersDesignEn !== "null" &&
                              product.SewingOrder.PreorderText.SheersDesignEn !== "" &&
                              product.SewingOrder.PreorderText.SewingModelId === "0099" && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Material & Color")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? product.SewingOrder.PreorderText.SheersDesignFa +
                                        "/" +
                                        product.SewingOrder.PreorderText.SheersColorFa
                                      : capitalizeFirstLetter(
                                          product.SewingOrder.PreorderText.SheersDesignEn
                                        ) +
                                        "/" +
                                        product.SewingOrder.PreorderText.SheersColorEn}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.PrivacyLayerHeaderStyle && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Header Style")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        Grommet: "پانجی",
                                        "Standard Lining": "آستردوزی استاندارد",
                                        Right: "راست",
                                        "Inverted Box Pleat": "پلیسه",
                                        "Pencil Pleat": "ساده",
                                        "Double European Pleat": "پلیسه دوچین اروپایی",
                                      }[product.SewingOrder.PreorderText.PrivacyLayerHeaderStyle] ||
                                      product.SewingOrder.PreorderText.PrivacyLayerHeaderStyle
                                    : product.SewingOrder.PreorderText.PrivacyLayerHeaderStyle}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.PanelTypeC && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Panel Type")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        "Single Panel": "تک پنل، سمت چپ",
                                        "Standard Lining": "آستردوزی استاندارد",
                                        "Pair, Split Draw": "جفت پنل،دو طرف",
                                        "Single Panel, Right": "تک پنل، سمت راست",
                                      }[product.SewingOrder.PreorderText.PanelTypeC] ||
                                      product.SewingOrder.PreorderText.PanelTypeC
                                    : product.SewingOrder.PreorderText.PanelTypeC}
                                </span>
                              </div>
                            )}
                            {shouldShowLayerHardware(
                              product.SewingOrder?.PreorderText,
                              PRIVACY_HARDWARE_FIELDS
                            ) && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Hardware")}</p>
                                <span>
                                  {getLayerHardwareLabel(
                                    product.SewingOrder.PreorderText,
                                    pageLanguage,
                                    PRIVACY_HARDWARE_FIELDS
                                  )}
                                </span>
                              </div>
                            )}
                            {product.MotorPositionC && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Motor Position")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? product.MotorPositionC === "Right"
                                      ? "راست"
                                      : product.MotorPositionC === "Left"
                                      ? "چپ"
                                      : product.MotorPositionC
                                    : product.MotorPositionC}
                                </span>
                              </div>
                            )}
                            {product.RemoteNameC && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Remote Name")}</p>
                                <span>{product.RemoteNameC}</span>
                              </div>
                            )}
                            {product.MotorChannelsC && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Motor Channels")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? product.MotorChannelsC.join(",").replace(
                                        /\d/g,
                                        (d) => "۰۱۲۳۴۵۶۷۸۹"[d]
                                      )
                                    : product.MotorChannelsC.join(",")}
                                </span>
                              </div>
                            )}
                            {product?.SewingOrder?.PreorderText?.SewingModelId === "0099" &&
                              product?.SewingOrder?.PreorderText?.Accessories?.some(
                                (accessory) => accessory?.DesignNameEn
                              ) && (
                                <div style={{ marginTop: "0.5rem" }}>
                                  <hr />
                                  <h1
                                    class="cart_agree_item_fabrics_sorted_title"
                                    style={{ marginTop: "0.2rem" }}
                                  >
                                    {t("Accessories")}
                                  </h1>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.Accessories &&
                              product.SewingOrder.PreorderText.SewingModelId !== "0325" &&
                              product.SewingOrder.PreorderText.Accessories.map(
                                (accessory, index) => {
                                  if (!accessory.DesignNameEn) {
                                    // اگر DesignNameEn مقدار نداشت، چیزی برنگردان
                                    return null;
                                  }
                                  return (
                                    <div
                                      key={index}
                                      className={`lable_Details ${
                                        pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                      }`}
                                    >
                                      <p>
                                        {pageLanguage === "fa"
                                          ? accessory.DesignNameFa
                                          : accessory.DesignNameEn}
                                      </p>
                                      <span>
                                        {pageLanguage === "fa"
                                          ? `تعداد ${new Intl.NumberFormat("fa-IR").format(
                                              accessory.Qty
                                            )}`
                                          : `Qty ${accessory.Qty}`}
                                      </span>
                                    </div>
                                  );
                                }
                              )}

                            {product.SewingOrder.PreorderText.ControlType && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Control Type")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? product.SewingOrder.PreorderText.ControlType ===
                                      "Motorized / Standard"
                                      ? "برقی / استاندارد"
                                      : product.SewingOrder.PreorderText.ControlType ===
                                        "Continuous Loop"
                                      ? "دستی"
                                      : product.SewingOrder.PreorderText.ControlType === "Motorized"
                                      ? "برقی"
                                      : product.SewingOrder.PreorderText.ControlType
                                    : product.SewingOrder.PreorderText.ControlType}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.ControlPosition && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Control Position")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        Right: "راست",
                                        Left: "چپ",
                                      }[product.SewingOrder.PreorderText.ControlPosition] ||
                                      product.SewingOrder.PreorderText.ControlPosition
                                    : product.SewingOrder.PreorderText.ControlPosition}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.ChainLength && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Chain Length")}</p>
                                <span>
                                  {pageLanguage === "fa" ? (
                                    <>
                                      {new Intl.NumberFormat("fa-IR").format(
                                        product.SewingOrder.PreorderText.ChainLength
                                      )}
                                    </>
                                  ) : (
                                    <>{product.SewingOrder.PreorderText.ChainLength}</>
                                  )}
                                  <>{t("cm")}</>
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.Headrail &&
                              product.SewingOrder.PreorderText.SewingModelId === "0324" && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Headrail")}</p>
                                  <span>{t(product.SewingOrder.PreorderText.Headrail)}</span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.MotorPosition && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Motor Position")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? product.SewingOrder.PreorderText.MotorPosition === "Right"
                                      ? "راست"
                                      : product.SewingOrder.PreorderText.MotorPosition === "Left"
                                      ? "چپ"
                                      : product.SewingOrder.PreorderText.MotorPosition
                                    : product.SewingOrder.PreorderText.MotorPosition}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.RemoteName && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Remote Name")}</p>
                                <span>{product.SewingOrder.PreorderText.RemoteName}</span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.MotorChannels && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Motor Channels")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? product.SewingOrder.PreorderText.MotorChannels.join(
                                        ","
                                      ).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d])
                                    : product.SewingOrder.PreorderText.MotorChannels.join(",")}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.MetalValanceStyle && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Metal Valance Style")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? product.SewingOrder.PreorderText.MetalValanceStyle ===
                                      "MetalValance"
                                      ? "قاب فلزی"
                                      : product.SewingOrder.PreorderText.MetalValanceStyle ===
                                        "MetalValanceFabricInsert"
                                      ? "قاب فلزی با نمای پارچه ای"
                                      : product.SewingOrder.PreorderText.MetalValanceStyle ===
                                        "Left"
                                      ? "چپ"
                                      : product.SewingOrder.PreorderText.MetalValanceStyle
                                    : product.SewingOrder.PreorderText.MetalValanceStyle}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.MetalValanceColor && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p> {t("Metal Valance Color")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        Black: "سیاه",
                                        White: "سفید",
                                        Silver: "نقره ای",
                                      }[product.SewingOrder.PreorderText.MetalValanceColor] ||
                                      product.SewingOrder.PreorderText.MetalValanceColor
                                    : product.SewingOrder.PreorderText.MetalValanceColor}
                                </span>
                              </div>
                            )}

                            {product.SewingOrder.PreorderText.RollType &&
                              shouldShow0325DisplayField(
                                product.SewingOrder.PreorderText,
                                "RollType"
                              ) && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Control Position")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? {
                                          Standard: "استاندارد",
                                          Premium: "اعلا",
                                        }[product.SewingOrder.PreorderText.RollType] ||
                                        product.SewingOrder.PreorderText.RollType
                                      : product.SewingOrder.PreorderText.RollType}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.BracketType &&
                              shouldShow0325DisplayField(
                                product.SewingOrder.PreorderText,
                                "BracketType"
                              ) && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Bracket Type")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? {
                                          "Round Edge": "گردگوشه",
                                          Left: "چپ",
                                        }[product.SewingOrder.PreorderText.BracketType] ||
                                        product.SewingOrder.PreorderText.BracketType
                                      : product.SewingOrder.PreorderText.BracketType}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.BracketColor &&
                              shouldShow0325DisplayField(
                                product.SewingOrder.PreorderText,
                                "BracketColor"
                              ) && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Bracket Color")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? {
                                          "Satin Brass": "طلایی",
                                          Left: "چپ",
                                        }[product.SewingOrder.PreorderText.BracketColor] ||
                                        product.SewingOrder.PreorderText.BracketColor
                                      : product.SewingOrder.PreorderText.BracketColor}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.HemStyle && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Hem Style")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        Straight: "صاف",
                                        Left: "چپ",
                                        Wave: "مواج",
                                        Scallop: "دایره‌ای",
                                        Traditional: "کنگره‌دار",
                                      }[product.SewingOrder.PreorderText.HemStyle] ||
                                      product.SewingOrder.PreorderText.HemStyle
                                    : product.SewingOrder.PreorderText.HemStyle}
                                </span>
                              </div>
                            )}
                            {product.SewingOrder.PreorderText.BottomBarStyle &&
                              shouldShow0325DisplayField(
                                product.SewingOrder.PreorderText,
                                "BottomBarStyle"
                              ) && (
                                <div
                                  className={`lable_Details ${
                                    pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                  }`}
                                >
                                  <p>{t("Bottom Bar Style")}</p>
                                  <span>
                                    {pageLanguage === "fa"
                                      ? {
                                          "Sewn-In": "دوخته شده داخل پارچه",
                                          Left: "چپ",
                                        }[product.SewingOrder.PreorderText.BottomBarStyle] ||
                                        product.SewingOrder.PreorderText.BottomBarStyle
                                      : product.SewingOrder.PreorderText.BottomBarStyle}
                                  </span>
                                </div>
                              )}
                            {product.SewingOrder.PreorderText.Features && (
                              <div
                                className={`lable_Details ${
                                  pageLanguage === "fa" ? "lable_Details_rtl" : ""
                                }`}
                              >
                                <p>{t("Features")}</p>
                                <span>
                                  {pageLanguage === "fa"
                                    ? {
                                        Standard: "استاندارد",
                                        Premium: "اعلا",
                                      }[product.SewingOrder.PreorderText.Features] ||
                                      t(product.SewingOrder.PreorderText.Features)
                                    : t(product.SewingOrder.PreorderText.Features)}
                                </span>
                              </div>
                            )}
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </>
                ) : (
                  <>
                    <h4 className="checkout_item_room_name" style={{ padding: "0.1rem 0" }}></h4>
                    <Accordion>
                      <Accordion.Item eventKey="0">
                        <ContextAwareToggleViewDetails
                          eventKey="0"
                          textOnHide={t("View Details")}
                          textOnShow={t("Hide Details")}
                        />
                        <Accordion.Body className="basket_item_title_dropdown dk_curtain_preview_dropdown">
                          <div className="basket_item_title_container 24">
                            <>
                              <span className="checkout_item_discount checkout_item_details_color">
                                <span>{t("Color: ")}</span>
                                {pageLanguage === "fa" ? product.ColorName : product.ColorEnName}
                              </span>
                              {product.ProductSizeName && (
                                <h2 className="checkout_item_details checkout_item_details_design">
                                  <span>{t("Size: ")}</span>
                                  {pageLanguage === "fa"
                                    ? convertToPersian(product.ProductSizeName)
                                    : product.ProductSizeEnName}
                                </h2>
                              )}

                              {product.ItemLength && (
                                <h2 className="checkout_item_details checkout_item_details_design">
                                  <span>
                                    {pageLanguage === "fa" ? "طول: " : "Length: "}
                                    &nbsp;
                                  </span>
                                  {pageLanguage === "fa" ? product.ItemLength : product.ItemLength}
                                  <span> {t("CM")}</span>
                                </h2>
                              )}
                              {product.Diameter && (
                                <h2 className="checkout_item_details checkout_item_details_design">
                                  <span>{t("Diameter: ")}</span>
                                  {pageLanguage === "fa" ? product.Diameter : product.Diameter}
                                  <span> {t("MM")}</span>
                                </h2>
                              )}
                              {product.LiningTypeEnName !== undefined &&
                                product.LiningTypeEnName != null &&
                                product.SewingModelId !== "0303" &&
                                product.SewingModelId !== "0324" &&
                                product.SewingModelId !== "0325" && (
                                  <h2 className="checkout_item_details checkout_item_details_design">
                                    <span>{t("Lining: ")}</span>
                                    {pageLanguage === "fa"
                                      ? product.LiningTypeName
                                      : product.LiningTypeEnName}
                                  </h2>
                                )}
                              {product.ControlPosition !== undefined &&
                                product.ControlPosition != null &&
                                (product.SewingModelId === "0325" ||
                                  product.SewingModelId === "0324" ||
                                  product.SewingModelId === "0303" ||
                                  product.SewingModelId === "0327" ||
                                  product.SewingModelId === "0328") && (
                                  <h2 className="checkout_item_details checkout_item_details_design">
                                    <span>{t("Control Position: ")}</span>
                                    {product.ControlPosition ? t("Right") : t("Left")}
                                  </h2>
                                )}
                            </>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </>
                )}
              </>

              {product.Discount > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                  <img
                    src={require("../Images/public/discount1.svg").default}
                    className="img-fluid discount_applied_img"
                    alt=""
                  />

                  <span className="descount_price">
                    <span>
                      {pageLanguage === "fa"
                        ? product.DiscountCode || product.DiscountName
                        : product.DiscountCode || product.DiscountEnName}
                    </span>
                    <span style={{ padding: "0 2px" }}>
                      ({GetPrice(-product.Discount, pageLanguage, t("T"), isTabletOrMobile)})
                    </span>
                  </span>
                </div>
              )}
            </div>
          </li>
        ))}
    </div>
  );
}
